import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, MapPin, DollarSign, Users, Image, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { createEvent, getCategories } from '../lib/api';
import { cn } from '../lib/utils';
import { format } from 'date-fns';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';

export default function CreateEventPage() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    event_date: null,
    event_time: '18:00',
    duration_hours: 2,
    location: '',
    fee: 0,
    capacity: null,
    is_women_only: false,
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const res = await getCategories();
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.description || !form.category || !form.event_date || !form.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Combine date and time
      const dateTime = new Date(form.event_date);
      const [hours, minutes] = form.event_time.split(':');
      dateTime.setHours(parseInt(hours), parseInt(minutes));

      const eventData = {
        title: form.title,
        description: form.description,
        category: form.category,
        event_date: dateTime.toISOString(),
        duration_hours: form.duration_hours,
        location: form.location,
        fee: form.fee || 0,
        capacity: form.capacity || null,
        is_women_only: form.is_women_only,
      };

      await createEvent(eventData);
      toast.success('Event created successfully!');
      navigate('/my-events');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border/50">
        <div className="flex items-center gap-4 p-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Create Event</h1>
        </div>
      </header>

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 space-y-6 max-w-lg mx-auto"
        onSubmit={handleSubmit}
      >
        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title">Event Title *</Label>
          <Input
            id="title"
            data-testid="event-title-input"
            placeholder="What's happening?"
            value={form.title}
            onChange={(e) => handleChange('title', e.target.value)}
            maxLength={50}
            className="h-12"
          />
          <p className="text-xs text-muted-foreground text-right">{form.title.length}/50</p>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            data-testid="event-description-input"
            placeholder="Tell people what to expect..."
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
            maxLength={500}
            rows={4}
          />
          <p className="text-xs text-muted-foreground text-right">{form.description.length}/500</p>
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={(v) => handleChange('category', v)}>
            <SelectTrigger className="h-12" data-testid="event-category-select">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  {cat.icon} {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Date *</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  data-testid="event-date-btn"
                  className={cn('w-full h-12 justify-start text-left font-normal', !form.event_date && 'text-muted-foreground')}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.event_date ? format(form.event_date, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={form.event_date}
                  onSelect={(d) => handleChange('event_date', d)}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-2">
            <Label>Time *</Label>
            <div className="relative">
              <Clock className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="time"
                data-testid="event-time-input"
                value={form.event_time}
                onChange={(e) => handleChange('event_time', e.target.value)}
                className="h-12 pl-10"
              />
            </div>
          </div>
        </div>

        {/* Duration */}
        <div className="space-y-2">
          <Label>Duration (hours)</Label>
          <Select value={String(form.duration_hours)} onValueChange={(v) => handleChange('duration_hours', parseInt(v))}>
            <SelectTrigger className="h-12" data-testid="event-duration-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8].map((h) => (
                <SelectItem key={h} value={String(h)}>
                  {h} hour{h > 1 ? 's' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Location */}
        <div className="space-y-2">
          <Label htmlFor="location">Location *</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="location"
              data-testid="event-location-input"
              placeholder="Where is it happening?"
              value={form.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className="h-12 pl-10"
            />
          </div>
        </div>

        {/* Fee */}
        <div className="space-y-2">
          <Label htmlFor="fee">Entry Fee (£)</Label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="fee"
              data-testid="event-fee-input"
              type="number"
              min="0"
              max="99.99"
              step="0.01"
              placeholder="0.00 (Free)"
              value={form.fee || ''}
              onChange={(e) => handleChange('fee', parseFloat(e.target.value) || 0)}
              className="h-12 pl-10"
            />
          </div>
          <p className="text-xs text-muted-foreground">Leave empty or 0 for free events. Payments are P2P.</p>
        </div>

        {/* Capacity */}
        <div className="space-y-2">
          <Label htmlFor="capacity">Capacity (optional)</Label>
          <div className="relative">
            <Users className="absolute left-3 top-3.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="capacity"
              data-testid="event-capacity-input"
              type="number"
              min="1"
              placeholder="Unlimited"
              value={form.capacity || ''}
              onChange={(e) => handleChange('capacity', parseInt(e.target.value) || null)}
              className="h-12 pl-10"
            />
          </div>
        </div>

        {/* Women Only */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-card border border-border">
          <div>
            <Label htmlFor="women-only" className="font-medium">Women Only Event</Label>
            <p className="text-sm text-muted-foreground">Restrict this event to women only</p>
          </div>
          <Switch
            id="women-only"
            data-testid="event-women-only-switch"
            checked={form.is_women_only}
            onCheckedChange={(v) => handleChange('is_women_only', v)}
          />
        </div>

        {/* Submit Button - Clearly visible */}
        <div className="pt-4 pb-28">
          <Button
            type="submit"
            data-testid="create-event-btn"
            className="w-full h-14 text-lg font-bold bg-pink-500 hover:bg-pink-600 text-white rounded-xl shadow-lg"
            disabled={loading}
          >
            {loading ? 'Creating...' : '🎉 Post Event'}
          </Button>
        </div>
      </motion.form>

      <BottomNav />
    </div>
  );
}
